import { baseApi } from "../../../../app/api";
import { transformCheckoutResponse } from "../helpers/transformCheckoutResponse";
import type {
  RawCheckoutResponse,
  ProcessCheckoutPayload,
} from "../../../../types/api/wcStoreApi/wcStoreApiCheckoutEndpointsTypes";
import type { TransformedCheckoutData } from "../types/checkoutApiTypes";

const WC_STORE_API_BASE =
  window.commonVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

export const checkoutApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetches the initial checkout state from the server.
     * This includes a draft order, customer addresses, and the currently available payment methods.
     * Endpoint: GET /wc/store/v1/checkout
     * @returns {TransformedCheckoutData} The transformed checkout data object.
     */
    getCheckoutData: builder.query<TransformedCheckoutData, void>({
      query: () => ({
        url: `${WC_STORE_API_BASE}/checkout`,
      }),
      transformResponse: (response: RawCheckoutResponse) =>
        transformCheckoutResponse(response),
      providesTags: ["Checkout"],
    }),

    /**
     * Updates the payment method on the server.
     * Note: Since the Store API recalculates totals on this request, we must send the
     * existing 'customerNote' to prevent it from being overwritten/wiped by the backend.
     */
    updatePaymentMethod: builder.mutation<
      TransformedCheckoutData,
      { paymentMethod: string; customerNote?: string }
    >({
      query: ({ paymentMethod, customerNote }) => ({
        url: `${WC_STORE_API_BASE}/checkout`,
        method: "PUT", // The Store API Checkout endpoint uses PUT for updates
        body: {
          payment_method: paymentMethod,
          order_notes: customerNote, // Required by Store API PUT docs
          customer_note: customerNote, // Sent for safety/consistency with POST payload
        },
        // This is crucial: it tells WC to recalculate everything
        params: { __experimental__calc_totals: true },
      }),
      transformResponse: (response: RawCheckoutResponse) =>
        transformCheckoutResponse(response),
      // Invalidate Cart so the Sidebar/Summary totals update everywhere
      invalidatesTags: ["Checkout", "Cart"],
    }),

    /**
     * Submits the final order for processing and payment.
     * This endpoint converts the draft order into a real order and initiates payment.
     * Endpoint: POST /wc/store/v1/checkout
     * @param {ProcessCheckoutPayload} payload - The complete checkout data, including addresses and payment details.
     * @returns {TransformedCheckoutData} The final order data, including the payment result and redirect URL if applicable.
     */
    processCheckout: builder.mutation<
      TransformedCheckoutData,
      ProcessCheckoutPayload
    >({
      query: (payload) => ({
        url: `${WC_STORE_API_BASE}/checkout`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: RawCheckoutResponse) =>
        transformCheckoutResponse(response),
      // Success means the cart is empty -> Invalidate Cart.
      // Success means the checkout session is closed -> Invalidate Checkout.
      invalidatesTags: ["Checkout", "Cart", "Order"],
    }),
  }),
});

export const {
  useGetCheckoutDataQuery,
  useUpdatePaymentMethodMutation,
  useProcessCheckoutMutation,
} = checkoutApi;
